import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Heart, Shield, Activity } from "lucide-react";
import { useHaptics } from "@/hooks/useHaptics";

const DISCLAIMER_ACCEPTED_KEY = "ybr_health_disclaimer_accepted";

interface HealthDisclaimerModalProps {
  forceShow?: boolean;
  onAccepted?: () => void;
}

export const HealthDisclaimerModal = ({ forceShow, onAccepted }: HealthDisclaimerModalProps) => {
  const [open, setOpen] = useState(false);
  const [healthAccepted, setHealthAccepted] = useState(false);
  const [emergencyAccepted, setEmergencyAccepted] = useState(false);
  const { medium } = useHaptics();

  useEffect(() => {
    if (forceShow) {
      setOpen(true);
      return;
    }
    
    const alreadyAccepted = localStorage.getItem(DISCLAIMER_ACCEPTED_KEY);
    if (!alreadyAccepted) {
      setOpen(true);
    }
  }, [forceShow]);

  const canAccept = healthAccepted && emergencyAccepted;

  const handleAccept = () => {
    if (!canAccept) return;
    
    medium();
    localStorage.setItem(DISCLAIMER_ACCEPTED_KEY, new Date().toISOString());
    setOpen(false);
    onAccepted?.();
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-md mx-auto max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-3">
          <div className="flex items-center justify-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            Aviso Importante de Saúde
          </DialogTitle>
          <DialogDescription className="text-center">
            Por favor, leia e aceite os termos antes de usar o app
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4" style={{ minHeight: '360px', contain: 'layout style', willChange: 'auto' }}>
          {/* Aviso de Saúde */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2" style={{ minHeight: '120px', contain: 'layout style' }}>
            <div className="flex items-center gap-2 text-destructive">
              <Heart className="w-5 h-5 flex-shrink-0" />
              <span className="font-semibold text-sm">Aviso Médico</span>
            </div>
            <p className="text-sm text-muted-foreground">
              O YouBetterRun <strong>não substitui orientação médica profissional</strong>. 
              Consulte um médico antes de iniciar qualquer programa de exercícios, especialmente 
              se você tem condições cardíacas, respiratórias ou outras condições de saúde.
            </p>
            <p className="text-sm text-muted-foreground">
              Métricas como calorias, zonas de frequência cardíaca e estimativas de desempenho 
              são <strong>aproximações</strong> e não devem ser usadas para diagnóstico médico.
            </p>
          </div>

          {/* Aviso de Emergência */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2" style={{ minHeight: '110px', contain: 'layout style' }}>
            <div className="flex items-center gap-2 text-amber-500">
              <Shield className="w-5 h-5 flex-shrink-0" />
              <span className="font-semibold text-sm">Função SOS</span>
            </div>
            <p className="text-sm text-muted-foreground">
              A função SOS do app é um <strong>recurso auxiliar</strong> e 
              <strong> NÃO substitui os serviços de emergência oficiais</strong> (SAMU 192, 
              Bombeiros 193, Polícia 190).
            </p>
            <p className="text-sm text-muted-foreground">
              Em caso de emergência real, <strong>ligue imediatamente para os serviços 
              de emergência</strong>. O app pode ter falhas de conexão ou cobertura.
            </p>
          </div>

          {/* Aviso de Atividade */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2" style={{ minHeight: '90px', contain: 'layout style' }}>
            <div className="flex items-center gap-2 text-primary">
              <Activity className="w-5 h-5 flex-shrink-0" />
              <span className="font-semibold text-sm">Atividade Física</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Corra sempre em locais seguros e bem iluminados. Esteja atento ao trânsito 
              e às condições climáticas. Interrompa o exercício imediatamente se sentir 
              dor, tontura ou falta de ar.
            </p>
          </div>

          {/* Checkboxes de Aceite */}
          <div className="space-y-3 pt-2">
            <div className="flex items-start gap-3">
              <Checkbox
                id="health-accept"
                checked={healthAccepted}
                onCheckedChange={(checked) => setHealthAccepted(checked as boolean)}
              />
              <label
                htmlFor="health-accept"
                className="text-sm cursor-pointer leading-tight"
              >
                Entendo que este app <strong>não substitui orientação médica</strong> e 
                que devo consultar um profissional de saúde antes de iniciar atividades físicas.
              </label>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="emergency-accept"
                checked={emergencyAccepted}
                onCheckedChange={(checked) => setEmergencyAccepted(checked as boolean)}
              />
              <label
                htmlFor="emergency-accept"
                className="text-sm cursor-pointer leading-tight"
              >
                Entendo que a função SOS <strong>não substitui serviços de emergência 
                oficiais</strong> (192, 193, 190) e que devo contactá-los diretamente em 
                situações de risco.
              </label>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            onClick={handleAccept}
            disabled={!canAccept}
            className="w-full"
            size="lg"
          >
            {canAccept ? "Li e Aceito os Termos" : "Aceite os termos para continuar"}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Ao continuar, você concorda com nossos{" "}
            <a href="/terms" className="underline">Termos de Uso</a> e{" "}
            <a href="/privacy" className="underline">Política de Privacidade</a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
